var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { buildDemoSummaryTeamAverageDiff } from '@web/lib/demo/utils';
import { emptyGmailSummaryAudienceCounts, SUMMARY_AUDIENCE_KEYS } from '@workalizer/shared';
import { getPercentageDiff, randomInt } from '@workalizer/shared/utils';
var toShare = function (part, whole) {
    return whole > 0 ? Math.round((part / whole) * 100) : 0;
};
var splitAudience = function (total) {
    if (total <= 0) {
        return emptyGmailSummaryAudienceCounts();
    }
    var inTeam = randomInt(Math.floor(total * 0.35), Math.floor(total * 0.55));
    var crossTeam = randomInt(Math.floor(total * 0.2), Math.floor(total * 0.35));
    var external = Math.max(0, total - inTeam - crossTeam);
    return {
        inTeam: inTeam,
        crossTeam: crossTeam,
        external: external
    };
};
var buildCountShareMetric = function (count, previousCount, shareDenominator, includeTeamAverage) {
    if (includeTeamAverage === void 0) { includeTeamAverage = false; }
    return (__assign({ count: count, share: toShare(count, shareDenominator), previous: getPercentageDiff(previousCount, count) }, (includeTeamAverage
        ? { teamAverage: buildDemoSummaryTeamAverageDiff(count) }
        : {})));
};
var buildRateMetric = function (rate, previousRate, includeTeamAverage) {
    if (includeTeamAverage === void 0) { includeTeamAverage = false; }
    return (__assign({ rate: rate, previous: getPercentageDiff(previousRate, rate) }, (includeTeamAverage
        ? { teamAverage: buildDemoSummaryTeamAverageDiff(rate) }
        : {})));
};
export var gmailSummary = function (input) {
    var includeTeamAverage = input.userId != null;
    var sent = randomInt(320, 520);
    var received = randomInt(480, 720);
    var replied = randomInt(Math.floor(received * 0.35), Math.floor(received * 0.6));
    var opened = randomInt(Math.floor(received * 0.55), Math.floor(received * 0.8));
    var previousSent = randomInt(280, 500);
    var previousReceived = randomInt(450, 700);
    var previousReplied = randomInt(Math.floor(previousReceived * 0.3), Math.floor(previousReceived * 0.55));
    var previousOpened = randomInt(Math.floor(previousReceived * 0.5), Math.floor(previousReceived * 0.75));
    var sentAudience = splitAudience(sent);
    var previousSentAudience = splitAudience(previousSent);
    var receivedAudience = splitAudience(received);
    var previousReceivedAudience = splitAudience(previousReceived);
    var internalSent = sentAudience.inTeam + sentAudience.crossTeam;
    var previousInternalSent = previousSentAudience.inTeam + previousSentAudience.crossTeam;
    var internalReplied = randomInt(Math.floor(internalSent * 0.4), Math.floor(internalSent * 0.65));
    var previousInternalReplied = randomInt(Math.floor(previousInternalSent * 0.35), Math.floor(previousInternalSent * 0.6));
    var internalNoReplyCount = randomInt(Math.floor(internalSent * 0.35), Math.floor(internalSent * 0.55));
    var previousInternalNoReplyCount = randomInt(Math.floor(previousInternalSent * 0.3), Math.floor(previousInternalSent * 0.5));
    var replyRate = toShare(internalReplied, internalSent);
    var previousReplyRate = toShare(previousInternalReplied, previousInternalSent);
    var volumeTotal = sent + received;
    var previousVolumeTotal = previousSent + previousReceived;
    var sentShare = toShare(sent, volumeTotal);
    var receivedShare = volumeTotal > 0 ? Math.max(0, 100 - sentShare) : 0;
    var previousSentShare = toShare(previousSent, previousVolumeTotal);
    var previousReceivedShare = previousVolumeTotal > 0 ? Math.max(0, 100 - previousSentShare) : 0;
    var forwarded = randomInt(20, 80);
    var previousForwarded = randomInt(15, 70);
    var buildVolumeMetric = function (count, previousCount, audience, previousAudience) { return (__assign(__assign({}, buildCountShareMetric(count, previousCount, count, includeTeamAverage)), { breakdown: Object.fromEntries(SUMMARY_AUDIENCE_KEYS.map(function (key) { return [
            key,
            {
                count: audience[key],
                share: toShare(audience[key], count),
                previous: getPercentageDiff(previousAudience[key], audience[key])
            }
        ]; })) })); };
    return {
        sent: buildVolumeMetric(sent, previousSent, sentAudience, previousSentAudience),
        received: buildVolumeMetric(received, previousReceived, receivedAudience, previousReceivedAudience),
        internalReplyRate: __assign(__assign({}, buildRateMetric(replyRate, previousReplyRate, includeTeamAverage)), { numerator: internalReplied, denominator: internalSent }),
        internalSentWithNoReply: buildCountShareMetric(internalNoReplyCount, previousInternalNoReplyCount, internalSent, includeTeamAverage),
        repliedEmails: buildCountShareMetric(replied, previousReplied, received, includeTeamAverage),
        openedEmails: buildCountShareMetric(opened, previousOpened, received, includeTeamAverage),
        sentVsReceivedBalance: {
            sentShare: sentShare,
            receivedShare: receivedShare,
            sentPrevious: getPercentageDiff(previousSentShare, sentShare),
            receivedPrevious: getPercentageDiff(previousReceivedShare, receivedShare)
        },
        forwarded: buildCountShareMetric(forwarded, previousForwarded, sent + received, includeTeamAverage)
    };
};
